import { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Filter, ArrowDownAZ, ArrowUpAZ, X } from "lucide-react";

export default function ColumnFilter({ col, values, selected, onChange, sortDir, onSort, onClearSort }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const numeric = col.type === "money" || col.type === "date";
  const active = (selected && selected.length >= 0 && selected !== null) || sortDir;
  const all = selected == null;
  const shown = values.filter((v) => v.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));
  const isChecked = (v) => all || selected.includes(v);
  const toggle = (v) => {
    let cur = all ? [...values] : [...selected];
    cur = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    onChange(cur.length === values.length ? null : cur);
  };
  const toggleAll = () => onChange(all ? [] : null);

  const MENU_W = 224; // w-56
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      let left = r.right - MENU_W;
      if (left < 8) left = 8;
      const maxLeft = window.innerWidth - MENU_W - 8;
      if (left > maxLeft) left = maxLeft;
      setPos({ top: r.bottom + 4, left });
    }
    setOpen(true);
  };

  // Menü açıkken viewport dışına taşarsa yeniden konumlandır (kaydırma/resize)
  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      let left = r.right - MENU_W;
      if (left < 8) left = 8;
      const maxLeft = window.innerWidth - MENU_W - 8;
      if (left > maxLeft) left = maxLeft;
      setPos({ top: r.bottom + 4, left });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={() => (open ? setOpen(false) : openMenu())}
        data-testid={`colmenu-${col.key}`}
        className={`p-0.5 rounded-sm hover:bg-[#E7DECC] ${active ? "text-[#B8933F]" : "text-[#A79A86]"}`}
      >
        <Filter className="w-3.5 h-3.5" />
      </button>
      {open && pos && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
          <div
            style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_W }}
            className="bg-white border border-[#E7DECC] rounded-sm shadow-lg z-[91] p-2 text-left font-normal normal-case tracking-normal max-h-[75vh] overflow-auto flex flex-col"
          >
            <button onClick={() => { onSort("asc"); setOpen(false); }} data-testid={`sort-asc-${col.key}`} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-[#EFE7D6] rounded-sm shrink-0">
              <ArrowDownAZ className="w-4 h-4 text-[#B8933F]" /> {numeric ? "Küçükten Büyüğe" : "A → Z"}
            </button>
            <button onClick={() => { onSort("desc"); setOpen(false); }} data-testid={`sort-desc-${col.key}`} className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-[#EFE7D6] rounded-sm shrink-0">
              <ArrowUpAZ className="w-4 h-4 text-[#B8933F]" /> {numeric ? "Büyükten Küçüğe" : "Z → A"}
            </button>
            <div className="border-t border-[#E7DECC] my-1.5 shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={`colfilter-${col.key}`}
              placeholder="Ara..."
              className="w-full px-2 py-1.5 text-sm border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F] mb-1.5 shrink-0"
            />
            <label className="flex items-center gap-2 px-2 py-1 text-sm font-medium cursor-pointer hover:bg-[#EFE7D6] rounded-sm shrink-0">
              <input type="checkbox" checked={all} onChange={toggleAll} data-testid={`colall-${col.key}`} className="w-4 h-4 accent-[#B8933F]" />
              (Tümünü Seç)
            </label>
            <div className="flex-1 min-h-0 overflow-auto mt-0.5">
              {shown.length === 0 ? (
                <div className="px-2 py-2 text-xs text-[#A79A86]">Sonuç yok</div>
              ) : shown.map((v) => (
                <label key={v} className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer hover:bg-[#EFE7D6] rounded-sm">
                  <input type="checkbox" checked={isChecked(v)} onChange={() => toggle(v)} data-testid={`colval-${col.key}-${v}`} className="w-4 h-4 accent-[#B8933F]" />
                  <span className={`truncate ${numeric ? "tabular-nums" : ""}`}>{v || "(boş)"}</span>
                </label>
              ))}
            </div>
            {(active) && (
              <button onClick={() => { onChange(null); onClearSort && onClearSort(); setOpen(false); }} data-testid={`clear-col-${col.key}`} className="w-full mt-1.5 flex items-center gap-2 px-2 py-1.5 text-sm text-[#A63A3A] hover:bg-[#A63A3A]/10 rounded-sm border-t border-[#E7DECC] pt-2 shrink-0">
                <X className="w-4 h-4" /> Filtreyi Temizle
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
